import { Routes } from '@angular/router';
import {
  ActivationComponent,
  ForgotPasswordComponent,
  loginRoutes,
  ResetPasswordComponent,
  TwoFactorComponent,
} from '@capturum/login';
import {
  BaseDataResolver,
  IndexedDbGuard,
  ModuleResolver,
  PermissionGuard,
  PublicTranslationResolver,
  SettingResolver,
  TranslationResolver,
} from '@capturum/complete';
import { AuthGuard } from '@capturum/auth';
import { NgxPermissionsGuard } from 'ngx-permissions';
import { BasicLayoutComponent } from '@shared/modules/layout/layouts/basic-layout/basic-layout.component';
import { AppRoutes } from '@core/enums/routes.enum';
import { ProposalStatusLeadComponent } from './features/leads/components/proposal-status-lead/proposal-status-lead.component';
import { UserAgreementComponent } from './features/user-agreement/components/user-agreement/user-agreement.component';
import { UserAgreementGuard } from './features/user-agreement/guards/user-agreement.guard';
import { UserAgreementHasAgreedGuard } from './features/user-agreement/guards/user-agreement-has-agreed.guard';
import { LoginLayoutComponent } from '@shared/modules/layout/layouts/login-layout/login-layout.component';
import { LgPublicTranslationResolver } from '@core/resolvers/lg-public-translation.resolver';
import { UserTypeGuard } from '@core/guards/user-type.guard';
import { LgProposalResolver } from '@core/resolvers/lg-proposal.resolver';
import { gtmInitResolver } from '@features/all-stars/resolvers/gtm-init.resolver';
import { LgLoginComponent } from '@shared/modules/layout/components/login/lg-login.component';
import { SsoAllStarsComponent } from '@shared/modules/layout/components/sso-all-stars/sso-all-stars.component';
import { CookiebotGuard } from '@shared/guards/cookie-bot.guard';
import {
  QUESTIONNAIRE_TENANT_OVERRIDE,
  QUESTIONNAIRE_ROUTE_PREFIX,
  QUESTIONNAIRE_LOCALE_PATH_INDEX,
} from './public/questionnaire/tokens/questionnaire-tenant.token';

export const routes: Routes = [
  {
    path: '',
    canActivate: [IndexedDbGuard, AuthGuard, PermissionGuard],
    resolve: {
      translations: TranslationResolver,
      baseData: BaseDataResolver,
      settings: SettingResolver,
      modules: ModuleResolver,
    },
    children: [
      {
        path: AppRoutes.userAgreement,
        canActivate: [UserAgreementHasAgreedGuard],
        component: UserAgreementComponent,
      },
      {
        path: '',
        component: BasicLayoutComponent,
        canActivate: [UserAgreementGuard],
        children: [
          {
            path: '',
            redirectTo: AppRoutes.dashboard,
            pathMatch: 'full',
          },
          {
            path: AppRoutes.dashboard,
            canActivate: [UserTypeGuard],
            loadChildren: () => {
              return import('./features/dashboard/dashboard.module').then((m) => {
                return m.DashboardModule;
              });
            },
          },
          {
            path: AppRoutes.distributor,
            canActivate: [NgxPermissionsGuard],
            loadChildren: () => {
              return import('./features/distributors/distributors.module').then((m) => {
                return m.DistributorsModule;
              });
            },
            data: {
              permissions: {
                only: 'distributor.manage.tenant',
                redirectTo: AppRoutes.dashboard,
              },
            },
          },
          {
            path: AppRoutes.installer,
            canActivate: [NgxPermissionsGuard],
            loadChildren: () => {
              return import('./features/installers/installers.module').then((m) => {
                return m.InstallersModule;
              });
            },
            data: {
              permissions: {
                only: 'installer.manage.tenant',
                redirectTo: AppRoutes.dashboard,
              },
            },
          },
          {
            path: AppRoutes.lead,
            loadChildren: () => {
              return import('./features/leads/leads.module').then((m) => {
                return m.LeadsModule;
              });
            },
          },
          {
            path: AppRoutes.translation,
            loadChildren: () => {
              return import('./features/translation/translation.module').then((m) => {
                return m.TranslationModule;
              });
            },
          },
          {
            path: AppRoutes.products,
            loadChildren: () => {
              return import('./features/product/products.module').then((m) => {
                return m.ProductsModule;
              });
            },
          },
          {
            path: AppRoutes.user,
            loadChildren: () => {
              return import('./features/user/user.module').then((m) => {
                return m.UserModule;
              });
            },
          },
          {
            path: AppRoutes.baseData,
            loadChildren: () => {
              return import('./features/base-data/base-data.module').then((m) => {
                return m.BaseDataModule;
              });
            },
            canActivate: [NgxPermissionsGuard],
            data: {
              permissions: {
                only: 'base-data-value.manage.tenant',
                redirectTo: '/',
              },
            },
          },
          {
            path: AppRoutes.config,
            loadChildren: () => {
              return import('./features/config/config.module').then((m) => {
                return m.ConfigModule;
              });
            },
          },
          {
            path: AppRoutes.builders.path,
            loadChildren: () => {
              return import('@capturum/builders/pages').then((m) => {
                return m.CapturumBuilderPagesModule;
              });
            },
          },
        ],
      },
    ],
  },
  {
    ...(({ loadComponent: _lc, component: _c, ...rest }) => {
      return rest;
    })(loginRoutes[0] as any),
    component: LoginLayoutComponent,
    canActivate: [IndexedDbGuard],
    resolve: {
      publicTranslations: LgPublicTranslationResolver,
    },
    children: [
      ...loginRoutes[0].children.map((route) => {
        const { component, loadComponent, ...cleanRoute } = route as any;

        return {
          ...cleanRoute,
          loadComponent: async () => {
            switch (route.path) {
              case AppRoutes.login:
                return LgLoginComponent;
              case AppRoutes.forgotPassword:
                return ForgotPasswordComponent;
              case AppRoutes.twoFactor:
                return TwoFactorComponent;
              case AppRoutes.resetPasswordTokenEmail:
                return ResetPasswordComponent;
              case AppRoutes.resetPasswordToken:
                return ResetPasswordComponent;
              case AppRoutes.activation:
                return ActivationComponent;
              default:
                break;
            }
          },
        };
      }),
      {
        path: AppRoutes.ssoAllStars,
        component: SsoAllStarsComponent,
      },
      {
        path: AppRoutes.allStars,
        loadChildren: () => {
          return import('./features/all-stars/all-stars.module').then((m) => {
            return m.AllStarsModule;
          });
        },
      },
    ],
  },
  {
    path: AppRoutes.review,
    loadChildren: () => {
      return import('./features/review/review.module').then((m) => {
        return m.ReviewModule;
      });
    },
    canActivate: [IndexedDbGuard],
    resolve: {
      publicTranslations: PublicTranslationResolver,
    },
  },
  {
    path: `${AppRoutes.proposedLead}/:token/:status`,
    component: ProposalStatusLeadComponent,
    canActivate: [IndexedDbGuard],
    resolve: {
      proposalData: LgProposalResolver,
    },
  },
  {
    path: AppRoutes.invitation,
    loadChildren: () => {
      return import('./public/invitation/invitation.module').then((m) => {
        return m.InvitationModule;
      });
    },
    canActivate: [IndexedDbGuard],
    resolve: {
      publicTranslations: LgPublicTranslationResolver,
    },
  },
  {
    path: AppRoutes.projectInformation,
    loadComponent: () => {
      return import('./public/project-information/pages/project-information/project-information.component').then(
        (m) => {
          return m.ProjectInformationComponent;
        }
      );
    },
    canActivate: [IndexedDbGuard],
    resolve: {
      publicTranslations: LgPublicTranslationResolver,
    },
  },
  // Test routes for questionnaire tenant + locale combinations
  {
    path: 'questionnaire/airco/:locale',
    loadChildren: () => {
      return import('./public/questionnaire/questionnaire.module').then((m) => {
        return m.QuestionnaireModule;
      });
    },
    canActivate: [IndexedDbGuard, CookiebotGuard],
    resolve: {
      publicTranslations: LgPublicTranslationResolver,
      gtm: gtmInitResolver,
    },
    providers: [
      { provide: QUESTIONNAIRE_TENANT_OVERRIDE, useValue: 'airco' },
      { provide: QUESTIONNAIRE_ROUTE_PREFIX, useValue: '/questionnaire/airco' },
      { provide: QUESTIONNAIRE_LOCALE_PATH_INDEX, useValue: 3 },
    ],
  },
  {
    path: 'questionnaire/heatpump/:locale',
    loadChildren: () => {
      return import('./public/questionnaire/questionnaire.module').then((m) => {
        return m.QuestionnaireModule;
      });
    },
    canActivate: [IndexedDbGuard, CookiebotGuard],
    resolve: {
      publicTranslations: LgPublicTranslationResolver,
      gtm: gtmInitResolver,
    },
    providers: [
      { provide: QUESTIONNAIRE_TENANT_OVERRIDE, useValue: 'warmtepomp' },
      { provide: QUESTIONNAIRE_ROUTE_PREFIX, useValue: '/questionnaire/heatpump' },
      { provide: QUESTIONNAIRE_LOCALE_PATH_INDEX, useValue: 3 },
    ],
  },
  // Should always be at the end of the routing file
  {
    path: ':locale',
    loadChildren: () => {
      return import('./public/questionnaire/questionnaire.module').then((m) => {
        return m.QuestionnaireModule;
      });
    },
    canActivate: [IndexedDbGuard, CookiebotGuard],
    resolve: {
      publicTranslations: LgPublicTranslationResolver,
      gtm: gtmInitResolver,
    },
  },
];
